import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useLanguage } from '../contexts/LanguageContext';
import { getText } from '../utils/convertText';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { ArrowRight, Check } from 'lucide-react';
import SEO from '../components/SEO';
import { companySchema, getPageMetadata, SITE } from '../config/seo';

const HomePage = () => {
  const { language } = useLanguage();

  const features = [
    {
      icon: '🌊',
      title: { tc: '源頭追溯', cn: '源头追溯', en: 'Source Traceability' },
      desc: { tc: '嚴選全球優質供應商，確保所有海產食品來源安全可靠', cn: '严选全球优质供应商，确保所有海产食品来源安全可靠', en: 'Carefully selected premium global suppliers to ensure all seafood products are safe and reliable' }
    },
    {
      icon: '🚚',
      title: { tc: '專業物流', cn: '专业物流', en: 'Professional Logistics' },
      desc: { tc: '採用歐盟標準冷凍車隊，透過全程低溫配送，確保海鮮新鮮度', cn: '采用欧盟标准冷冻车队，透过全程低温配送，确保海鲜新鲜度', en: 'EU-standard refrigerated fleet with full cold chain delivery to ensure seafood freshness' }
    },
    {
      icon: '❄️',
      title: { tc: '先進冷鏈及倉儲', cn: '先进冷链及仓储', en: 'Advanced Cold Chain & Storage' },
      desc: { tc: '專業冷凍設備，擁有自家大型水冷制冰機，使用淨化水製冰', cn: '专业冷冻设备，拥有自家大型水冷制冰机，使用净化水制冰', en: 'Professional refrigeration equipment with large-scale water-cooled ice maker using purified water' }
    },
    {
      icon: '🐟',
      title: { tc: '專業養殖設施', cn: '专业养殖设施', en: 'Professional Aquaculture' },
      desc: { tc: '設有專業低溫養殖池，配合先進水質監測系統，確保最鮮活海產品', cn: '设有专业低温养殖池，配合先进水质监测系统，确保最鲜活海产品', en: 'Professional low-temperature aquaculture pools with advanced water quality monitoring systems' }
    },
    {
      icon: '💻',
      title: { tc: 'ERP管理', cn: 'ERP管理', en: 'ERP Management' },
      desc: { tc: '採用 ERP/POS/APPS 數位化存倉系統，簡化營運流程', cn: '采用 ERP/POS/APPS 数位化存仓系统，简化营运流程', en: 'ERP/POS/APPS digital inventory system to streamline operations' }
    }
  ];

  const getLocalizedText = (texts) => texts[language] || texts.tc;
  const meta = getPageMetadata('home', language);
  const schemas = [
    companySchema(),
    { '@type': 'WebSite', name: SITE.name, url: SITE.url, publisher: { '@id': `${SITE.url}/#organization` } },
  ];

  return (
    <div className="min-h-screen">
      <SEO {...meta} path="/" language={language} schemas={schemas} />
      <Header />

      <section className="relative bg-gradient-to-br from-cyan-50 to-teal-50 py-20" data-testid="hero-section">
        <div className="container mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
            >
              <h1 className="text-5xl lg:text-6xl font-bold leading-tight mb-6" data-testid="hero-title">
                <span className="gradient-text">
                  {language === 'tc' ? '優質海鮮批發' : language === 'cn' ? '优质海鲜批发' : 'Premium Seafood Wholesale'}
                </span>
                <br />
                <span className="gradient-text">
                  {language === 'tc' ? 'ESG 永續經營' : language === 'cn' ? 'ESG 永续经营' : 'ESG Sustainable Business'}
                </span>
              </h1>
              <p className="text-lg text-slate-600 mb-8 leading-relaxed">
                {language === 'tc'
                  ? '樹記海鮮致力於提供最高標準的專業服務，堅守新鮮第一、客戶至上，並透過ESG策略推動香港餐飲業永續發展。'
                  : language === 'cn'
                  ? '树记海鲜致力于提供最高标准的专业服务，坚守新鲜第一、客户至上，并透过ESG策略推动香港餐饮业永续发展。'
                  : 'Shukee Seafood is committed to providing the highest standards of professional service, prioritizing freshness and customer satisfaction while promoting sustainable development in Hong Kong\'s catering industry through ESG strategies.'}
              </p>
              <Link to="/contact">
                <Button 
                  className="rounded-full px-8 py-6 text-lg bg-gradient-to-r from-primary to-secondary hover:shadow-xl transition-all"
                  data-testid="explore-products-btn"
                >
                  {language === 'tc' ? '發掘更多食材產品 聯絡我們為你深入解答' : language === 'cn' ? '发掘更多食材产品 联络我们为你深入解答' : 'Discover More Products - Contact Us for Details'}
                  <ArrowRight className="ml-2" size={20} />
                </Button>
              </Link>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="relative"
            >
              <img
                src="https://images.unsplash.com/photo-1615141982883-c7ad0e69fd62?w=800"
                alt="Fresh seafood display"
                className="rounded-[32px] shadow-2xl w-full object-cover"
                style={{ height: '450px' }}
              />
            </motion.div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-white" data-testid="seafood-section">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold mb-6 gradient-text">
              {language === 'tc' ? '我們的海鮮' : language === 'cn' ? '我们的海鲜' : 'Our Seafood'}
            </h2>
            <p className="text-lg text-slate-600 max-w-3xl mx-auto">
              {language === 'tc'
                ? '嚴選全球優質海產，從魚類到甲殼類，為您提供最新鮮的海洋美味'
                : language === 'cn'
                ? '严选全球优质海产，从鱼类到甲壳类，为您提供最新鲜的海洋美味'
                : 'Carefully selected premium seafood from around the world, from fish to crustaceans, bringing you the freshest ocean delicacies'}
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                image: 'https://customer-assets.emergentagent.com/job_seafood-cms/artifacts/4y5mqwcp_img_002.jpeg',
                title: { tc: '優質魚類', cn: '优质鱼类', en: 'Premium Fish' }
              },
              {
                image: 'https://customer-assets.emergentagent.com/job_seafood-cms/artifacts/lor4isb9_img_003.png',
                title: { tc: '波士頓龍蝦', cn: '波士顿龙虾', en: 'Boston Lobster' }
              },
              {
                image: 'https://customer-assets.emergentagent.com/job_seafood-cms/artifacts/2k6xx01c_img_001.jpeg',
                title: { tc: '優質蝦類', cn: '优质虾类', en: 'Premium Prawns' }
              }
            ].map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                viewport={{ once: true }}
                className="group relative overflow-hidden rounded-3xl shadow-xl hover:shadow-2xl transition-all duration-300"
              >
                <div className="aspect-square overflow-hidden">
                  <img
                    src={item.image}
                    alt={getLocalizedText(item.title)}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                </div>
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent flex flex-col justify-end p-6">
                  <h3 className="text-3xl font-bold text-white">{getLocalizedText(item.title)}</h3>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-gradient-to-br from-cyan-50 to-white" data-testid="esg-section">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <div className="inline-block bg-gradient-to-r from-primary to-secondary text-white px-6 py-2 rounded-full text-sm font-bold mb-4">
              ESG
            </div>
            <h2 className="text-4xl font-bold mb-6">
              {language === 'tc' ? '環境、社會及管治承諾' : language === 'cn' ? '环境、社会及管治承诺' : 'Environmental, Social & Governance Commitment'}
            </h2>
            <p className="text-lg text-slate-600 max-w-3xl mx-auto">
              {language === 'tc'
                ? '樹記海鮮致力推動可持續發展，通過「從海洋到餐桌」生態循環計畫，實現零浪費目標，並與本地農漁業界緊密合作，共創綠色未來。'
                : language === 'cn'
                ? '树记海鲜致力推动可持续发展，通过「从海洋到餐桌」生态循环计画，实现零浪费目标，并与本地农渔业界紧密合作，共创绿色未来。'
                : 'Shukee Seafood is committed to sustainable development through our "Sea to Table" ecosystem, achieving zero waste and collaborating with local agriculture for a greener future.'}
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
            {[
              {
                icon: '🌱',
                title: { tc: '環境保護', cn: '环境保护', en: 'Environment' },
                desc: { tc: '減少廚餘，推動循環經濟', cn: '减少厨余，推动循环经济', en: 'Reduce waste, promote circular economy' }
              },
              {
                icon: '🤝',
                title: { tc: '社會責任', cn: '社会责任', en: 'Social' },
                desc: { tc: '支持本地農漁業發展', cn: '支持本地农渔业发展', en: 'Support local agriculture' }
              },
              {
                icon: '⭐',
                title: { tc: '企業管治', cn: '企业管治', en: 'Governance' },
                desc: { tc: '透明誠信，品質保證', cn: '透明诚信，品质保证', en: 'Transparent and trustworthy' }
              }
            ].map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ delay: index * 0.1 }}
                viewport={{ once: true }}
                className="glass p-6 rounded-3xl text-center"
              >
                <div className="text-5xl mb-4">{item.icon}</div>
                <h3 className="text-xl font-bold mb-2">{getLocalizedText(item.title)}</h3>
                <p className="text-slate-600">{getLocalizedText(item.desc)}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20" data-testid="features-section">
        <div className="container mx-auto px-6">
          <h2 className="text-4xl font-bold text-center mb-16" data-testid="features-title">
            {language === 'tc' ? '五大品質保證' : language === 'cn' ? '五大品质保证' : 'Five Pillars of Quality Assurance'}
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                viewport={{ once: true }}
                className="glass p-6 rounded-3xl hover:shadow-xl transition-all duration-300 hover:-translate-y-2"
                data-testid={`feature-card-${index}`}
              >
                <div className="w-14 h-14 bg-gradient-to-br from-primary to-secondary rounded-2xl flex items-center justify-center text-2xl mb-4">
                  {feature.icon}
                </div>
                <h3 className="text-lg font-bold mb-2">{getLocalizedText(feature.title)}</h3>
                <p className="text-slate-600 leading-relaxed text-sm">{getLocalizedText(feature.desc)}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-gradient-to-r from-primary to-secondary py-20 text-white" data-testid="cta-section">
        <div className="container mx-auto px-6 text-center">
          <h2 className="text-4xl font-bold mb-6">
            {language === 'tc' ? '準備好合作了嗎？' : language === 'cn' ? '准备好合作了吗？' : 'Ready to Partner With Us?'}
          </h2>
          <p className="text-xl mb-8 opacity-90">
            {language === 'tc'
              ? '聯絡我們，了解更多專業海產服務'
              : language === 'cn'
              ? '联络我们，了解更多专业海产服务'
              : 'Contact us to learn more about our professional seafood services'}
          </p>
          <Link to="/contact">
            <Button 
              className="rounded-full px-10 py-6 text-lg bg-white text-primary hover:bg-slate-50"
              data-testid="contact-us-btn"
            >
              {language === 'tc' ? '聯絡我們' : language === 'cn' ? '联络我们' : 'Contact Us'}
            </Button>
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default HomePage;